import React, { useState, useEffect } from 'react';
import { ProbateWizard } from './components/ProbateWizard';
import { TestEstatesPage } from './components/dev/TestEstatesPage';
import { HomePage } from './components/HomePage';
import { AuthProvider, useAuth } from './components/auth/AuthContext';
import { AppTopBar } from './components/ui';
import { AuthModal } from './components/auth/AuthModal';
import { API_BASE } from './services/api';
import { Analytics } from '@vercel/analytics/react';
import { UserDashboard } from './components/auth/UserDashboard';
import { PrivacyPolicy } from './components/legal/PrivacyPolicy';
import { TermsOfService } from './components/legal/TermsOfService';
import { CookieConsent } from './components/legal/CookieConsent';
import { NotFound } from './components/NotFound';
import { UnlockingPage } from './components/UnlockingPage';
import { ProWorkspace } from './components/pro/ProWorkspace';

type ViewMode = 'home' | 'app' | 'dev-test' | 'privacy' | 'terms' | '404' | 'unlocking' | 'pro';
const ESTATE_ID_KEY = 'probate_current_estate_id';
const WIZARD_AUTOSAVE_KEY = 'probate_wizard_autosave_v1';

function AppContent() {
    const { user, isLoading, logout } = useAuth();
    // Show homepage by default; authenticated users go straight to the app
    const [viewMode, setViewMode] = useState<ViewMode>('home');
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authTab, setAuthTab] = useState<'login' | 'register'>('login');
    const [authMessage, setAuthMessage] = useState<string | null>(null);
    const [resetToken, setResetToken] = useState<string | null>(null);
    const [verifyBanner, setVerifyBanner] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
    const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');

    const handleResendVerification = async () => {
        setResendState('sending');
        try {
            await fetch(`${API_BASE}/api/auth/resend-verification`, {
                method: 'POST',
                credentials: 'include',
            });
            setResendState('sent');
        } catch {
            setResendState('idle');
        }
    };

    const showUnverifiedBanner = !!user && user.emailVerified === false;
    const [showDashboard, setShowDashboard] = useState(false);
    const [wizardKey, setWizardKey] = useState(0);
    // Track if user has explicitly entered the app this session
    const [enteredApp, setEnteredApp] = useState(false);
    // Estate we are waiting for Stripe to unlock — drives the 'unlocking' view
    const [unlockEstateId, setUnlockEstateId] = useState<string | null>(null);
    // Per-mount flag telling the wizard to skip the resume prompt and land
    // directly on the last saved step (used after Stripe unlock). Keyed to
    // wizardKey so we reset it the next time a fresh mount doesn't need it.
    const [wizardAutoResumeKey, setWizardAutoResumeKey] = useState<number | null>(null);

    // Detect URL path on mount — show 404 for unrecognised routes
    useEffect(() => {
        const path = window.location.pathname.replace(/\/+$/, '') || '/';
        const knownPaths = ['/', '/privacy', '/terms'];
        if (path === '/pro' || path.startsWith('/pro/')) {
            // Pro workspace — access enforced inside the render branch (we
            // need `user` and `isLoading` to be settled before deciding
            // between redirect-to-home and showing the workspace).
            setViewMode('pro');
        } else if (!knownPaths.includes(path)) {
            setViewMode('404');
        } else if (path === '/privacy') {
            setViewMode('privacy');
        } else if (path === '/terms') {
            setViewMode('terms');
        }
    }, []);

    // Re-evaluate the route on browser back/forward (and on programmatic
    // pushState within the Pro workspace, which dispatches popstate).
    useEffect(() => {
        const handler = () => {
            const path = window.location.pathname.replace(/\/+$/, '') || '/';
            if (path === '/pro' || path.startsWith('/pro/')) setViewMode('pro');
            else if (path === '/privacy') setViewMode('privacy');
            else if (path === '/terms') setViewMode('terms');
            else if (path === '/') setViewMode('home');
            else setViewMode('404');
        };
        window.addEventListener('popstate', handler);
        return () => window.removeEventListener('popstate', handler);
    }, []);

    // Listen for navigation events from CookieConsent banner
    useEffect(() => {
        const handler = (e: Event) => {
            const page = (e as CustomEvent).detail;
            if (page === 'privacy') setViewMode('privacy');
            if (page === 'terms') setViewMode('terms');
        };
        window.addEventListener('navigate', handler);
        return () => window.removeEventListener('navigate', handler);
    }, []);

    // Check for ?reset=TOKEN in URL on mount
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('reset');
        if (token) {
            setResetToken(token);
            setShowAuthModal(true);
            window.history.replaceState({}, '', window.location.pathname);
        }
        // If returning from a successful Stripe checkout, route to the dedicated
        // unlocking screen so the user sees a clear "we're confirming payment"
        // state while we poll for the webhook-granted entitlement. Without this,
        // the user was dropped into the old estate view while auth was still
        // resolving, which read as a logout.
        //
        // We deliberately leave the ?payment=success&estateId=X params in the
        // URL until the unlock completes. A mid-flight refresh then re-enters
        // the unlocking screen instead of silently dropping the user on the
        // home page with no context.
        const paymentParam = params.get('payment');
        const estateIdParam = params.get('estateId');
        if (paymentParam === 'success' && estateIdParam) {
            setUnlockEstateId(estateIdParam);
            setViewMode('unlocking');
        } else if (paymentParam) {
            // Cancelled or legacy — strip the param and leave the user where they were.
            window.history.replaceState({}, '', window.location.pathname);
        }
        // Handle email verification link ?verify=TOKEN
        const verifyTok = params.get('verify');
        if (verifyTok) {
            window.history.replaceState({}, '', window.location.pathname);
            fetch(`${API_BASE}/api/auth/verify-email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: verifyTok }),
            })
                .then(async (res) => {
                    const data = await res.json().catch(() => ({}));
                    if (res.ok) {
                        setVerifyBanner({ kind: 'success', text: 'Email verified — thank you.' });
                    } else {
                        setVerifyBanner({ kind: 'error', text: data.error || 'Verification link is invalid or has expired.' });
                    }
                })
                .catch(() => setVerifyBanner({ kind: 'error', text: 'Could not reach the server. Please try again.' }));
            setTimeout(() => setVerifyBanner(null), 6000);
        }
    }, []);

    // When user logs in and has previously clicked "Start", route into the app
    useEffect(() => {
        if (user && enteredApp && viewMode === 'home') {
            setViewMode('app');
        }
    }, [user, enteredApp]); // eslint-disable-line react-hooks/exhaustive-deps

    // On sign-out, leave the estate tool and return to the homepage. Without
    // this, the previous user's assessment stays on screen after logout.
    //
    // IMPORTANT: only fire on an *actual* logout (user was authenticated this
    // session, then became null). A transient `/me` failure — common when the
    // API cold-starts after a Stripe redirect — briefly sets `user = null`
    // even though the session cookie is still valid. Reacting to every null
    // used to clear localStorage and drop the user to the homepage after
    // payment, which read as "the app logged me out".
    const wasAuthenticatedRef = React.useRef(false);
    useEffect(() => {
        if (user) wasAuthenticatedRef.current = true;
    }, [user]);
    useEffect(() => {
        if (isLoading) return;
        if (!user && wasAuthenticatedRef.current) {
            wasAuthenticatedRef.current = false;
            setViewMode('home');
            setShowDashboard(false);
            setEnteredApp(false);
            setUnlockEstateId(null);
            localStorage.removeItem(ESTATE_ID_KEY);
            // Wizard autosave is an unscoped global key — must be cleared on
            // sign-out, otherwise the next account on the same browser
            // rehydrates the previous user's estate inputs on mount.
            localStorage.removeItem(WIZARD_AUTOSAVE_KEY);
            // Force ProbateWizard to remount with a clean slate next time.
            setWizardKey(k => k + 1);
        }
    }, [user, isLoading]);

    const openAuth = (tab: 'login' | 'register', message?: string) => {
        setAuthTab(tab);
        setAuthMessage(message ?? null);
        setShowAuthModal(true);
    };

    // Triggered by ProbateWizard when an anonymous user clicks Save.
    const handleWizardRequestAuth = (msg?: string) => {
        openAuth('register', msg);
    };

    const handleOpenCase = (estateId: string) => {
        localStorage.setItem(ESTATE_ID_KEY, estateId);
        setShowDashboard(false);
        setEnteredApp(true);
        setViewMode('app');
        // Auto-resume so clicking a report tile lands the user directly on
        // the last saved step (usually the results / full report) instead of
        // the welcome screen with a "Resume session" prompt.
        setWizardKey(k => {
            const next = k + 1;
            setWizardAutoResumeKey(next);
            return next;
        });
    };

    const handleNewCase = () => {
        localStorage.removeItem(ESTATE_ID_KEY);
        localStorage.removeItem(WIZARD_AUTOSAVE_KEY);
        setShowDashboard(false);
        setEnteredApp(true);
        setViewMode('app');
        setWizardKey(k => k + 1);
    };

    // Called by homepage CTA
    const handleStartFromHome = () => {
        setEnteredApp(true);
        if (user) {
            setViewMode('app');
        } else {
            // Show register modal; after auth, useEffect routes them in
            openAuth('register');
        }
    };

    // Infer which wizard categories to enable based on asset data
    const inferCategories = (assets: any) => {
        const cats: string[] = [];
        if (assets?.properties?.length > 0) cats.push('property');
        if (assets?.bank_accounts?.length > 0) cats.push('banks');
        if (assets?.investments?.length > 0) cats.push('investments');
        if (assets?.pensions?.length > 0) cats.push('pensions');
        if (assets?.debts?.length > 0) cats.push('debts');
        if (assets?.lifetime_gifts?.length > 0 || assets?.gifts_last_7_years?.exists) cats.push('gifts');
        if (assets?.bpr_assets?.length > 0) cats.push('bpr');
        if (assets?.apr_assets?.length > 0) cats.push('apr');
        return cats;
    };

    // ── Pro workspace ───────────────────────────────────────
    // Access rules:
    //   - anonymous → bounced to home (auth modal opened by handleStartFromHome
    //     pattern would be wrong here; we just send them home where they can
    //     sign in). Prevents an unauthed user from probing /pro/cases URLs.
    //   - consumer → bounced to home (no /pro access for the public tier).
    //   - pro/admin → render the workspace.
    // We wait for isLoading so a freshly-loaded /pro URL doesn't briefly
    // bounce a logged-in pro user before the session restore completes.
    if (viewMode === 'pro') {
        if (isLoading) {
            return <div className="min-h-screen bg-paper" />;
        }
        if (!user || (user.role !== 'pro' && user.role !== 'admin')) {
            window.history.replaceState({}, '', '/');
            // Defer setState to avoid setting state during render.
            queueMicrotask(() => setViewMode('home'));
            return <div className="min-h-screen bg-paper" />;
        }
        return <ProWorkspace />;
    }

    // ── Legal pages ─────────────────────────────────────────
    if (viewMode === 'privacy') {
        return <PrivacyPolicy onBack={() => setViewMode('home')} />;
    }
    if (viewMode === 'terms') {
        return <TermsOfService onBack={() => setViewMode('home')} />;
    }
    if (viewMode === '404') {
        return <NotFound onHome={() => { window.history.replaceState({}, '', '/'); setViewMode('home'); }} />;
    }

    // ── Stripe return: poll for webhook-confirmed unlock ─────
    if (viewMode === 'unlocking' && unlockEstateId) {
        return (
            <UnlockingPage
                estateId={unlockEstateId}
                onUnlocked={(id) => {
                    localStorage.setItem(ESTATE_ID_KEY, id);
                    window.history.replaceState({}, '', window.location.pathname);
                    setUnlockEstateId(null);
                    setEnteredApp(true);
                    setViewMode('app');
                    // Remount the wizard AND tell it to auto-resume this
                    // specific mount. Keying auto-resume to the new wizardKey
                    // guarantees it applies only to the post-unlock mount and
                    // survives StrictMode's dev-only double-invoke of effects.
                    setWizardKey(k => {
                        const next = k + 1;
                        setWizardAutoResumeKey(next);
                        return next;
                    });
                }}
                onCancel={() => {
                    window.history.replaceState({}, '', window.location.pathname);
                    setUnlockEstateId(null);
                    setViewMode('home');
                }}
            />
        );
    }

    // Note: the email-verification nag is now integrated into <AppTopBar> for the
    // App view (cleaner Chambers chrome). The homepage view is marketing-style
    // chrome and intentionally omits it — the user lands in the wizard quickly.

    // ── Homepage view ────────────────────────────────────────
    if (viewMode === 'home' && !isLoading) {
        return (
            <>
                <AppTopBar
                    viewMode="home"
                    onViewModeChange={setViewMode}
                    onWordmarkClick={() => setViewMode('home')}
                    onMyAccount={() => setShowDashboard(true)}
                    onOpenAuth={openAuth}
                    showDevTab={import.meta.env.DEV}
                    unverified={showUnverifiedBanner}
                    onResendVerification={handleResendVerification}
                    resendState={resendState}
                />
                <HomePage
                    onStart={handleStartFromHome}
                    onSignIn={() => openAuth('login')}
                    isAuthenticated={!!user}
                    onNavigate={(page: string) => {
                        if (page === 'privacy') setViewMode('privacy');
                        if (page === 'terms') setViewMode('terms');
                    }}
                    onOpenEstate={handleOpenCase}
                    onNewCase={handleNewCase}
                />

                {showAuthModal && (
                    <AuthModal
                        defaultTab={authTab}
                        resetToken={resetToken}
                        message={authMessage}
                        onClose={() => { setShowAuthModal(false); setResetToken(null); setAuthMessage(null); }}
                    />
                )}
                {verifyBanner && (
                    <div className={`fixed top-[100px] left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-lg shadow-lg text-sm font-medium ${
                        verifyBanner.kind === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'
                    }`}>
                        {verifyBanner.text}
                    </div>
                )}
                {showDashboard && (
                    <UserDashboard
                        onClose={() => setShowDashboard(false)}
                        onOpenCase={handleOpenCase}
                        onNewCase={handleNewCase}
                    />
                )}
            </>
        );
    }

    // ── App view (estate tool) ───────────────────────────────
    return (
        <div className="min-h-screen bg-paper flex flex-col">
            <AppTopBar
                viewMode={viewMode === 'dev-test' ? 'dev-test' : viewMode === 'app' ? 'app' : 'home'}
                onViewModeChange={setViewMode}
                onWordmarkClick={() => setViewMode('home')}
                onMyAccount={() => setShowDashboard(true)}
                onOpenAuth={openAuth}
                showDevTab={import.meta.env.DEV}
                unverified={showUnverifiedBanner}
                onResendVerification={handleResendVerification}
                resendState={resendState}
            />

            {/* Main content */}
            <main className="flex-1">
                {viewMode === 'dev-test' ? (
                    <TestEstatesPage
                        onBack={() => setViewMode('app')}
                        onLoadIntoAudit={(data) => {
                            const session = {
                                data,
                                selectedCategories: inferCategories(data.EstateAssets),
                                currentStep: 1,
                                savedAt: new Date().toISOString(),
                            };
                            localStorage.setItem('probate_wizard_autosave_v1', JSON.stringify(session));
                            localStorage.removeItem(ESTATE_ID_KEY);
                            setWizardKey(k => k + 1);
                            setViewMode('app');
                        }}
                    />
                ) : (
                    <ProbateWizard
                        key={wizardKey}
                        autoResume={wizardAutoResumeKey === wizardKey}
                        onRequestAuth={handleWizardRequestAuth}
                    />
                )}
            </main>

            {showAuthModal && (
                <AuthModal
                    defaultTab={authTab}
                    resetToken={resetToken}
                    message={authMessage}
                    onClose={() => { setShowAuthModal(false); setResetToken(null); setAuthMessage(null); }}
                />
            )}
            {showDashboard && (
                <UserDashboard
                    onClose={() => setShowDashboard(false)}
                    onOpenCase={handleOpenCase}
                    onNewCase={handleNewCase}
                />
            )}
        </div>
    );
}

function App() {
    return (
        <AuthProvider>
            <AppContent />
            <CookieConsent />
            <Analytics />
        </AuthProvider>
    );
}

export default App;
